import { Box, Button, Chip, CircularProgress, Divider, Typography } from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useConstants } from "../hooks/UseConstants";
import Fetch from "../services/Fetch";
import { useWaits } from "../hooks/UseWait";
import { FormattedMessage, useIntl } from "react-intl";
import {
    DetailInfoRow,
    DetailMediaPanel,
    DetailModalShell,
    DetailSection,
} from "../components/admin/AdminDetailParts";

function AdsDetails({ onClickClose, onClickUpdate, banner, setSnackBar, setBanners }) {
    const { language, host } = useConstants();
    const { sendWait, setSendWait } = useWaits();
    const intl = useIntl();
    const isAr = language === "ar";

    const getStatusMeta = (item) => {
        const now = new Date();
        const start = item?.active_from ? new Date(item.active_from) : null;
        const end = item?.active_until ? new Date(item.active_until) : null;

        if (start && now < start) {
            return { label: intl.formatMessage({ id: "not_started" }), color: "warning" };
        }
        if (!item?.is_active) {
            return { label: intl.formatMessage({ id: "stopped" }), color: "default" };
        }
        if (end && now > end) {
            return { label: intl.formatMessage({ id: "finished" }), color: "error" };
        }
        return { label: intl.formatMessage({ id: "active" }), color: "success" };
    };

    const formatDate = (value) => {
        if (!value) return isAr ? "غير متوفر" : "Unavailable";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return new Intl.DateTimeFormat(isAr ? "ar-EG" : "en-US", { dateStyle: "medium" }).format(parsed);
    };

    const deleteBanner = async () => {
        setSendWait(true);
        const result = await Fetch(host + `/admin/banners/${banner.id}/delete`, "DELETE", null);

        if (result.status === 200) {
            setBanners((banners) => banners.filter((ads) => ads.id !== banner.id));
            setSnackBar("success", isAr ? "تم حذف الإعلان بنجاح" : "Ad deleted successfully");
            onClickClose();
        } else {
            setSnackBar("error", result.data?.errors?.[0] || result.data?.message || "Error");
        }

        setSendWait(false);
    };

    if (!banner) return null;

    const statusMeta = getStatusMeta(banner);
    const title = language === "en" ? banner.name_en : banner.name_ar;
    const description = (language === "en" ? banner.description_en : banner.description_ar)
        || (isAr ? "لا يوجد وصف مضاف لهذا الإعلان." : "No description added for this ad.");
    const relatedName = language === "en" ? banner.related_data?.name_en : banner.related_data?.name_ar;
    const majorName = language === "en" ? banner.major?.name_en : banner.major?.name_ar;
    const degreeName = language === "en" ? banner.academic_degree?.name_en : banner.academic_degree?.name_ar;

    return (
        <DetailModalShell
            isAr={isAr}
            icon={<CampaignOutlinedIcon sx={{ fontSize: 32 }} />}
            title={<FormattedMessage id="ads_details" />}
            subtitle={isAr ? "مراجعة تفاصيل الإعلان قبل التعديل أو الحذف." : "Review ad information before editing or deleting it."}
            onClose={onClickClose}
        >
            <DetailMediaPanel
                image={banner.image}
                fallbackIcon={<CampaignOutlinedIcon sx={{ fontSize: 56, color: "#94a3b8" }} />}
                title={title}
                description={description}
                badges={[
                    {
                        key: "type",
                        label: banner.category === "course" ? intl.formatMessage({ id: "course" }) : intl.formatMessage({ id: "path" }),
                        sx: { bgcolor: "#eff6ff", color: "#0D358C", fontWeight: 700 },
                    },
                    {
                        key: "status",
                        label: statusMeta.label,
                        color: statusMeta.color,
                    },
                ]}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
                <DetailSection
                    icon={<LinkOutlinedIcon />}
                    title={isAr ? "الربط والاستهداف" : "Link & targeting"}
                >
                    <DetailInfoRow
                        label={isAr ? "نوع الإعلان" : "Ad type"}
                        value={banner.category === "course" ? intl.formatMessage({ id: "course" }) : intl.formatMessage({ id: "path" })}
                    />
                    <DetailInfoRow
                        label={`${intl.formatMessage({ id: "category" })} / ${intl.formatMessage({ id: "course" })}`}
                        value={relatedName || "-"}
                    />
                    <DetailInfoRow
                        label={isAr ? "التخصص" : "Major"}
                        value={majorName || (isAr ? "كل التخصصات" : "All majors")}
                    />
                    <DetailInfoRow
                        label={isAr ? "المرحلة الأكاديمية" : "Academic degree"}
                        value={degreeName || (isAr ? "كل المراحل" : "All degrees")}
                        isLast
                    />
                </DetailSection>

                <DetailSection
                    icon={<ScheduleOutlinedIcon />}
                    title={isAr ? "الجدولة والحالة" : "Schedule & status"}
                >
                    <DetailInfoRow label={intl.formatMessage({ id: "start_date" })} value={formatDate(banner.active_from)} />
                    <DetailInfoRow label={intl.formatMessage({ id: "end_date" })} value={formatDate(banner.active_until)} />
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.35 }}>
                            {intl.formatMessage({ id: "ads_state" })}
                        </Typography>
                        <Chip size="small" label={statusMeta.label} color={statusMeta.color} variant="outlined" />
                    </Box>
                </DetailSection>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end" }}>
                <Button
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    sx={{ minWidth: 140 }}
                    onClick={() => {
                        onClickUpdate(banner.id);
                        onClickClose();
                    }}
                >
                    <FormattedMessage id="update" />
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlineIcon />}
                    sx={{ minWidth: 150, boxShadow: "0 4px 14px rgba(220,38,38,0.28)" }}
                    onClick={deleteBanner}
                    disabled={sendWait}
                >
                    {sendWait ? (isAr ? "جاري الحذف..." : "Deleting...") : <FormattedMessage id="delete" />}
                </Button>
            </Box>
        </DetailModalShell>
    );
}

export default AdsDetails;
